import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router";

import AddIcon from "@mui/icons-material/Add";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";

import { Button } from "@covertree/ui";
import { useAccessPattern } from "@hooks/useAccessPattern";
import {
  BASE_NEW_POLICY_PATH,
  BULK_QUOTES_PATH,
  SEQUOIA_UPLOAD_PATH,
} from "@routing/paths";

export function TopBarMenu() {
  const navigate = useNavigate();
  const { canWriteBusiness, canCreateSequoiaQuotes } = useAccessPattern();

  const [open, setOpen] = useState<boolean>(false);
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
  } | null>(null);

  const exitMenu = useCallback(() => {
    setOpen(false);
    setContextMenu(null);
  }, [setOpen, setContextMenu]);

  const redirectToPath = useCallback(
    (path: string) => {
      navigate(path);
      exitMenu();
    },
    [navigate]
  );

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setOpen(!open);
      if (!open) {
        setContextMenu({
          mouseX: event.clientX + 2,
          mouseY: event.clientY - 6,
        });
      }
    },
    [open, setOpen, setContextMenu]
  );

  const menuItems = useMemo(
    () => [
      ...(canWriteBusiness
        ? [
            {
              label: "New Quote",
              clickHandler: () => redirectToPath(BASE_NEW_POLICY_PATH),
            },
            {
              label: "Bulk Quotes",
              clickHandler: () => redirectToPath(BULK_QUOTES_PATH),
            },
          ]
        : []),
      ...(canCreateSequoiaQuotes
        ? [
            {
              label: "Sequoia Upload",
              clickHandler: () => redirectToPath(SEQUOIA_UPLOAD_PATH),
            },
          ]
        : []),
    ],
    [redirectToPath, canWriteBusiness, canCreateSequoiaQuotes]
  );

  return (
    <>
      {!!menuItems.length && (
        <Button
          data-testid="topbar-add-menu-button"
          onClick={handleClick}
          sx={(theme) => ({
            border: `2px solid ${theme.colors.green2}`,
            backgroundColor: theme.colors.green2,
            color: theme.colors.white,
            lineHeight: "1em",
            "&:hover": {
              color: theme.colors.green2,
              border: `2px solid ${theme.colors.green2}`,
              backgroundColor: theme.colors.white,
            },
          })}
          size="small">
          <AddIcon />
          Create
        </Button>
      )}
      <Menu
        open={contextMenu !== null}
        onClose={exitMenu}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }>
        {menuItems.map(({ label, clickHandler }) => (
          <MenuItem key={label} onClick={clickHandler}>
            {label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
